/*
 * Fly-to-cart star trail (2026-07-06, user request).
 *
 * When an item is added to cart from ANY path — the PDP Add to Cart, the sticky
 * ATC bar, the sticky ATC variant sheet, or the cart-drawer "Complete the
 * Matching Set" upsell Add — a short trail of gold stars flies into the cart
 * drawer's item counter (which opens on every add) and bursts into a sparkle
 * there; it falls back to the header cart icon if the drawer counter is absent.
 *
 * Design note: rather than editing the four separate add scripts (product-form,
 * sticky-atc, cart-drawer, free-book-offer), this hooks the ONE thing they all
 * do on a server-confirmed success and nothing else does on failure: they
 * re-render the #cart-icon-bubble section's inner HTML. We record the clicked
 * button's position on click (captured before any re-render can destroy it),
 * then fire when the bubble re-renders. This means zero edits to the fragile
 * core cart JS and it fires only on real successes.
 */
(function () {
  if (window.__flyToCartInit) return;
  window.__flyToCartInit = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isEditor = window.Shopify && window.Shopify.designMode;
  if (reduce || isEditor) return;
  if (!('animate' in document.createElement('div')) || !('MutationObserver' in window)) return;

  // Clicking any of these means an add-to-cart was just initiated.
  var ATC_SELECTORS = [
    '.product-form__submit',
    '[data-sticky-atc-button]',
    '[data-sticky-atc-sheet-add]',
    '[data-bundle-builder-submit]',
    '[data-qa-add]',
    '[data-mb-add]',
    '[data-qa-trigger][data-qa-direct="true"]',
    '.qcart-upsell__add',
    'product-form [name="add"]',
    // Storybook product page bars (2026-10-30): they submit the product form with requestSubmit, which dispatches
    // no click on Add, so the bar's own tap is the origin.
    '[data-pdp-sb-add]'
  ].join(',');

  var GOLD = (getComputedStyle(document.documentElement).getPropertyValue('--brand-gold') || '').trim() || '#bfa181';

  var pending = null; // { x, y, t, oldCount, maxAge } — origin captured at click time
  var holding = false; // true while we're masking the badge with the old count
  var heldOld = null;  // the old count value we display during the flight
  var latestNew = null; // the newest real count seen (revealed at swell end)
  var safetyTimer = null; // the CURRENT cycle's failsafe reveal timer (one at a time)
  var quickOrigins = new WeakMap();
  var matchingOrigins = new WeakMap();

  function reveal() {
    holding = false;
    // Void any click captured during the hold that hasn't rendered yet — it
    // already joined this cycle's total; it must not spawn a late trail.
    pending = null;
    var c = document.querySelector('.qcart__count');
    if (c && latestNew != null) c.textContent = latestNew;
  }

  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest(ATC_SELECTORS) : null;
    if (!el) return;
    if (el.matches('[data-mb-add]') &&
      (el.getAttribute('aria-disabled') === 'true' || el.closest('.mb[aria-busy="true"]'))) return;
    if (el.matches('[data-qa-add], [data-qa-trigger]') &&
      (el.disabled || el.closest('[data-qa-card][aria-busy="true"], .qa-panel[aria-busy="true"]'))) return;
    // A bar tap that opens the size sheet (or does nothing) is no add: the page stamps each tap's route first.
    if (el.matches('[data-pdp-sb-add]') &&
      (el.getAttribute('aria-disabled') === 'true' || el.classList.contains('is-soldout') || el.getAttribute('data-pdp-sb-route') !== 'add')) return;
    var r = el.getBoundingClientRect();
    if (!r.width && !r.height) return;
    var counterNow = document.querySelector('.qcart__count');
    var origin = {
      x: r.left + r.width / 2,
      y: r.top + r.height / 2,
      t: Date.now(),
      oldCount: counterNow ? counterNow.textContent.trim() : null,
      // The bundle path performs authoritative Worker validation before its
      // one cart add. Preserve the origin through that expected extra latency;
      // existing add paths keep their proven three-second stale-click guard.
      maxAge: el.matches('[data-qa-add], [data-qa-trigger], [data-mb-add]') ? 120000 :
        (el.matches('[data-bundle-builder-submit]') ? 30000 : 3000)
    };
    // Quick add resolves gifts and may wait behind another card. An unrelated
    // drawer mutation must not consume its origin before its confirmed render.
    if (el.matches('[data-qa-add], [data-qa-trigger]')) quickOrigins.set(el, origin);
    else if (el.matches('[data-mb-add]')) matchingOrigins.set(el, origin);
    else pending = origin;
  }, true);

  document.addEventListener('quick-add-icon:render', function (event) {
    var control = event.detail && event.detail.control;
    var origin = control && quickOrigins.get(control);
    if (origin && Date.now() - origin.t <= origin.maxAge) pending = origin;
    if (control) quickOrigins.delete(control);
  });
  document.addEventListener('quick-add-icon:cancel', function (event) {
    if (event.detail && event.detail.control) quickOrigins.delete(event.detail.control);
  });

  document.addEventListener('matching-builder:render', function (event) {
    var control = event.detail && event.detail.control;
    var origin = control && matchingOrigins.get(control);
    if (origin && Date.now() - origin.t <= origin.maxAge) pending = origin;
    if (control) matchingOrigins.delete(control);
  });
  document.addEventListener('matching-builder:cancel', function (event) {
    if (event.detail && event.detail.control) matchingOrigins.delete(event.detail.control);
  });

  // A cart error means the add did NOT succeed — drop the pending origin so a
  // later unrelated re-render can't fire a stale trail.
  if (window.subscribe) {
    try { window.subscribe('cart-error', function () { pending = null; }); } catch (e) {}
  }

  // Watch the cart-drawer subtree: it re-renders on EVERY add path (and its
  // counter is our fly target). Crucially this also catches SECONDARY
  // re-renders that touch the drawer but not the header bubble — e.g. a
  // free-gift auto-add — so the count hold can re-pin on those too. Falls back
  // to the header cart icon if there is no drawer (cart-notification mode).
  var watchTarget = document.querySelector('cart-drawer') || document.getElementById('cart-icon-bubble');
  if (!watchTarget) return;

  var layer = null;
  function getLayer() {
    if (layer && layer.isConnected) return layer;
    layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483000;overflow:hidden;';
    document.body.appendChild(layer);
    return layer;
  }

  function visibleCounter() {
    var counter = document.querySelector('.qcart__count');
    var drawer = counter && counter.closest('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return null;
    var r = counter.getBoundingClientRect();
    var style = getComputedStyle(counter);
    return r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 &&
      r.left < window.innerWidth && r.top < window.innerHeight &&
      style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0' ? counter : null;
  }

  function targetPoint() {
    // Land in the MIDDLE of the cart drawer's item counter — the drawer opens on
    // every add, so it's the natural destination. Fall back to the header cart
    // icon if the drawer counter isn't present/visible (e.g. cart-notification
    // mode, or the drawer somehow isn't open).
    var el = visibleCounter();
    var r = el ? el.getBoundingClientRect() : null;
    var center = true;
    if (!r || (!r.width && !r.height)) {
      el = document.getElementById('cart-icon-bubble');
      r = el ? el.getBoundingClientRect() : null;
      center = false; // the icon sparkle sits toward its top-right, not dead center
    }
    if (!r) return null;
    var x = center ? r.left + r.width / 2 : r.left + r.width * 0.62;
    var y = center ? r.top + r.height / 2 : r.top + r.height * 0.4;
    // Clamp into the viewport so an off-screen target still reads sensibly.
    x = Math.max(8, Math.min(x, window.innerWidth - 8));
    y = Math.max(8, Math.min(y, window.innerHeight - 8));
    return { x: x, y: y };
  }

  function makeStar(size) {
    var s = document.createElement('span');
    s.textContent = '\u2726';
    s.style.cssText =
      'position:fixed;left:0;top:0;will-change:transform,opacity;pointer-events:none;' +
      'color:' + GOLD + ';font-size:' + size + 'px;line-height:1;' +
      'text-shadow:0 0 4px rgba(191,161,129,0.55);';
    return s;
  }

  function flyTrail(from, to) {
    var lyr = getLayer();
    var count = 6;
    var dx = to.x - from.x, dy = to.y - from.y;
    var len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
    var px = -dy / len, py = dx / len; // unit perpendicular, for a gentle arc
    var lastIdx = count - 1;
    var bursted = false;
    for (var i = 0; i < count; i++) {
      (function (idx) {
        var size = 9 + Math.random() * 6;
        var star = makeStar(size);
        lyr.appendChild(star);
        var sx = from.x + (Math.random() - 0.5) * 18;
        var sy = from.y + (Math.random() - 0.5) * 18;
        var arc = (40 + Math.random() * 50) * (from.y >= to.y ? 1 : -1);
        var mx = (sx + to.x) / 2 + px * arc;
        var my = (sy + to.y) / 2 + py * arc - 30; // lift so it arcs upward
        // keep every waypoint inside the viewport so nothing flies off the edge
        var CLAMP = 10;
        sx = Math.max(CLAMP, Math.min(sx, window.innerWidth - CLAMP));
        mx = Math.max(CLAMP, Math.min(mx, window.innerWidth - CLAMP));
        var dur = 1450 + Math.random() * 400;
        var anim = star.animate([
          { transform: 'translate(' + sx + 'px,' + sy + 'px) scale(1) rotate(0deg)', opacity: 0 },
          { opacity: 1, offset: 0.12 },
          { transform: 'translate(' + mx + 'px,' + my + 'px) scale(1.05) rotate(90deg)', offset: 0.55 },
          { transform: 'translate(' + to.x + 'px,' + to.y + 'px) scale(0.35) rotate(200deg)', opacity: 0.9 }
        ], { duration: dur, delay: idx * 105, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'both' });
        anim.onfinish = function () {
          star.remove();
          // Start the swell as the FIRST star lands (while the rest are still
          // pouring in) rather than after the last one; fire once.
          if (!bursted) { bursted = true; burst(to); }
        };
        // A canceled animation (e.g. external DOM teardown) never finishes --
        // clean up without triggering the burst.
        anim.oncancel = function () { star.remove(); };
      })(i);
    }
  }

  function burst(at) {
    // If we landed on the drawer counter, swell the badge ITSELF — it grows and
    // settles with a soft glow as the stars pour in, as if absorbing them. A
    // gold ring/stars would be invisible on the gold badge. The displayed number
    // ticks up to the new count at the END of the swell.
    var counter = visibleCounter();
    if (counter) {
      var cr = counter.getBoundingClientRect();
      if (cr.width || cr.height) {
        var swell = counter.animate([
          { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(191,161,129,0)' },
          { transform: 'scale(1.28)', boxShadow: '0 0 0 8px rgba(191,161,129,0.35)', offset: 0.5 },
          { transform: 'scale(1)', boxShadow: '0 0 0 12px rgba(191,161,129,0)' }
        ], { duration: 950, easing: 'ease-in-out', fill: 'none' });
        swell.onfinish = reveal;
        return;
      }
    }
    // Fallback (header cart icon target): a gold ring + a few radiating stars,
    // with every endpoint clamped inside the viewport.
    var lyr = getLayer();
    var C = 10;
    var ring = document.createElement('span');
    ring.style.cssText =
      'position:fixed;left:' + (at.x - 6) + 'px;top:' + (at.y - 6) + 'px;width:12px;height:12px;' +
      'border-radius:50%;border:2px solid ' + GOLD + ';pointer-events:none;box-sizing:border-box;';
    lyr.appendChild(ring);
    var ringAnim = ring.animate([
      { transform: 'scale(0.4)', opacity: 0.9 },
      { transform: 'scale(2.4)', opacity: 0 }
    ], { duration: 640, easing: 'cubic-bezier(0.2,0.7,0.3,1)', fill: 'forwards' });
    ringAnim.onfinish = ringAnim.oncancel = function () { ring.remove(); };
    var n = 5;
    for (var i = 0; i < n; i++) {
      (function (idx) {
        var st = makeStar(7 + Math.random() * 3);
        lyr.appendChild(st);
        var ang = (Math.PI * 2 * idx) / n + Math.random() * 0.4;
        var dist = 14 + Math.random() * 12;
        var ex = Math.max(C, Math.min(at.x + Math.cos(ang) * dist, window.innerWidth - C));
        var ey = Math.max(C, Math.min(at.y + Math.sin(ang) * dist, window.innerHeight - C));
        var stAnim = st.animate([
          { transform: 'translate(' + at.x + 'px,' + at.y + 'px) scale(0.2)', opacity: 1 },
          { transform: 'translate(' + ex + 'px,' + ey + 'px) scale(1)', opacity: 0 }
        ], { duration: 560, easing: 'cubic-bezier(0.3,0.6,0.3,1)', fill: 'forwards' });
        stAnim.onfinish = stAnim.oncancel = function () { st.remove(); };
      })(i);
    }
  }

  var obs = new MutationObserver(function () {
    // Nothing pending and nothing masked -- the common case for every drawer
    // mutation outside an add cycle (quantity edits, note toggles, upsells).
    // Bail before any DOM work so this observer is near-free for the session.
    if (!holding && !pending) return;
    var counter = document.querySelector('.qcart__count');
    // Already masking: a LATER cart re-render (e.g. a free-gift auto-add) just
    // tried to paint the new count. Remember the latest real total for the
    // reveal, and keep the badge pinned to the old value. Runs synchronously in
    // this observer microtask, before paint, so no flicker.
    if (holding) {
      if (counter) {
        var t = counter.textContent.trim();
        // Only react to a REAL re-render to a new value; ignore our own re-pin
        // writes (which read back as heldOld) so we never clobber latestNew or
        // loop. This runs in the observer microtask (before paint) so re-pinning
        // stays flicker-free even for the secondary re-render.
        if (heldOld != null && t !== heldOld) {
          latestNew = t;
          counter.textContent = heldOld;
        }
      }
      // An add clicked while a hold is already active JOINS the current cycle:
      // its total lands via latestNew at this cycle's reveal. Chaining a second
      // full trail (the old behavior) kept the badge pinned at the stale number
      // for another whole flight — rapid drawer-upsell adds read as "stuck".
      pending = null;
      return;
    }
    if (!pending) return;
    if (Date.now() - pending.t > pending.maxAge) { pending = null; return; }
    var from = { x: pending.x, y: pending.y };
    heldOld = pending.oldCount;
    pending = null;
    latestNew = counter ? counter.textContent.trim() : null;
    // Hold the OLD count IMMEDIATELY (synchronously, before paint) so the badge
    // never flickers to the new value; the swell reveals the final count.
    if (counter && heldOld != null && heldOld !== latestNew) {
      counter.textContent = heldOld;
      holding = true;
    }
    // Wait for the drawer to finish sliding in (0.7s ease) before the trail —
    // unless it is ALREADY open (drawer-upsell adds: the 'active' class sits on
    // the persistent host before this render; fresh opens only add it a
    // macrotask later), where waiting just reads as lag.
    var drawerHost = document.querySelector('cart-drawer');
    var wait = drawerHost && drawerHost.classList.contains('active') ? 60 : 720;
    setTimeout(function () {
      var to = targetPoint();
      if (to) flyTrail(from, to);
      // Safety: reveal the real count even if the swell never runs (bg tab).
      // Keep exactly ONE timer alive -- a leftover timer from a previous add
      // cycle would otherwise fire mid-hold of THIS cycle and reveal early.
      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(reveal, 4000);
    }, wait);
  });
  obs.observe(watchTarget, { childList: true, subtree: true });
})();
